import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Lock, Chrome, Facebook, Apple, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/store/useAuth';
import { redirectToPanel, getPostLoginReturnPath } from '@/utils/panelRedirect';

const BrandPanel = () => (
  <div className="relative hidden overflow-hidden hero-gradient noise-overlay lg:flex lg:flex-col lg:justify-end lg:p-10">
    <div>
      <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=900&q=80" alt="Modern home" className="mb-8 h-56 w-full rounded-2xl object-cover shadow-2xl" />
      <h2 className="font-display text-3xl font-bold text-white">Your next home is closer than you think.</h2>
      <p className="mt-2 max-w-md text-white/85">Save favorite homes, set instant price-drop alerts, and message trusted local agents — all in one place.</p>
      <div className="mt-6 flex items-center gap-2 text-white/90">
        <div className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={16} className="fill-amber-400 text-amber-400" />)}</div>
        <span className="text-sm">Trusted by 2M+ home shoppers</span>
      </div>
    </div>
  </div>
);

const SocialButtons = ({ onSuccess = () => redirectToPanel({ force: true }) }) => {
  const loginWithGoogle = useAuth((s) => s.loginWithGoogle);
  const loginWithFacebook = useAuth((s) => s.loginWithFacebook);
  const loginWithApple = useAuth((s) => s.loginWithApple);

  const handleGoogle = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast.error('Google login is not configured.');
      return;
    }
    if (!window.google?.accounts?.oauth2?.initTokenClient) {
      toast.error('Google login is not available. Please refresh and try again.');
      return;
    }
    try {
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'openid email profile',
        callback: async (resp) => {
          if (resp?.error || !resp?.access_token) {
            toast.error(resp?.error_description || 'Google sign-in was cancelled.');
            return;
          }
          try {
            await loginWithGoogle({ access_token: resp.access_token });
            toast('Welcome back!');
            onSuccess();
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Google login failed. Please try again.');
          }
        },
        error_callback: (err) => {
          if (err?.type === 'popup_closed') {
            toast.error('Google sign-in popup was closed.');
            return;
          }
          if (err?.type === 'popup_failed_to_open') {
            toast.error('Popup blocked. Allow popups for casahom.com and try again.');
            return;
          }
          toast.error(err?.message || 'Google sign-in failed. Please try again.');
        },
      });
      tokenClient.requestAccessToken({ prompt: 'select_account' });
    } catch (e) {
      toast.error(e?.message || 'Google login failed. Please try again.');
    }
  };

  const handleFacebook = () => {
    if (!window.FB || !process.env.REACT_APP_FACEBOOK_APP_ID) {
      toast('Facebook login is not available yet');
      return;
    }
    try {
      window.FB.login(
        (resp) => {
          if (resp.authResponse) {
            loginWithFacebook(resp.authResponse.accessToken)
              .then(() => {
                toast('Welcome back!');
                onSuccess();
              })
              .catch((e) => toast(e?.response?.data?.detail || 'Facebook login failed'));
          }
        },
        { scope: 'email' }
      );
    } catch (e) {
      toast(e?.response?.data?.detail || 'Facebook login failed');
    }
  };

  const handleApple = async () => {
    if (!window.AppleID || !process.env.REACT_APP_APPLE_CLIENT_ID) {
      toast('Apple login is not available yet');
      return;
    }
    try {
      window.AppleID.auth.init({
        clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
        scope: 'email name',
        redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI,
        usePopup: true,
      });
      const res = await window.AppleID.auth.signIn();
      await loginWithApple(res.authorization.id_token, res.user);
      toast('Welcome back!');
      onSuccess();
    } catch (e) {
      toast(e?.response?.data?.detail || 'Apple login failed');
    }
  };

  const providers = [
    { name: 'Google', Icon: Chrome, handler: handleGoogle, enabled: !!process.env.REACT_APP_GOOGLE_CLIENT_ID },
    { name: 'Facebook', Icon: Facebook, handler: handleFacebook, enabled: !!process.env.REACT_APP_FACEBOOK_APP_ID },
    { name: 'Apple', Icon: Apple, handler: handleApple, enabled: !!process.env.REACT_APP_APPLE_CLIENT_ID },
  ].filter((p) => p.enabled);

  if (providers.length === 0) return null;

  return (
    <div className={`grid gap-3 ${providers.length === 1 ? 'grid-cols-1' : providers.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
      {providers.map(({ Icon, name, handler }) => (
        <button key={name} type="button" onClick={handler} className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid={`social-${name.toLowerCase()}`}>
          <Icon size={18} /><span className="hidden sm:inline">{name}</span>
        </button>
      ))}
    </div>
  );
};

export default function Login() {
  const authLogin = useAuth((s) => s.login);
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const afterLogin = () => {
    const returnPath = getPostLoginReturnPath(location);
    if (returnPath) {
      navigate(returnPath, { replace: true });
      return;
    }
    redirectToPanel({ force: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) errs.email = 'Enter a valid email';
    if (form.password.length < 6) errs.password = 'Password must be at least 6 characters';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      await authLogin(form.email, form.password);
      toast('Welcome back!');
      afterLogin();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        'Invalid email or password';
      setErrors({ api: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
      <BrandPanel />
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold text-slate-900">Sign in</h1>
          <p className="mt-1 text-slate-600">Welcome back. Enter your details to continue.</p>
          <div className="mt-6"><SocialButtons onSuccess={afterLogin} /></div>
          {(process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_FACEBOOK_APP_ID || process.env.REACT_APP_APPLE_CLIENT_ID) ? (
            <div className="my-6 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />OR<span className="h-px flex-1 bg-slate-200" /></div>
          ) : (
            <div className="mt-6" />
          )}
          {errors.api && <p className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{errors.api}</p>}
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5"><Mail size={16} className="absolute left-3 top-3 text-slate-400" /><Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="rounded-xl pl-9" data-testid="login-email" /></div>
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-blue-600 hover:underline">Forgot password?</Link>
              </div>
              <div className="relative mt-1.5"><Lock size={16} className="absolute left-3 top-3 text-slate-400" /><Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="rounded-xl pl-9" data-testid="login-password" /></div>
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
            </div>
            <Button type="submit" className="w-full rounded-xl" disabled={loading} data-testid="auth-submit-button">
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-600">Don&rsquo;t have an account? <Link to="/register" className="font-medium text-blue-600 hover:underline">Create one</Link></p>
        </div>
      </div>
    </div>
  );
}

export { BrandPanel, SocialButtons };
